import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { APIResponseBFFCollection } from "@superagents/types/api";
import type { DocID } from "@/types";

type ChatsInfinite = InfiniteData<
  APIResponseBFFCollection<"bff::chatWithLastAttendance">
>;

/** Query da lista "Não lidas" (SA-1455): `unread: true` na key de `useChats`. */
export const isUnreadListKey = (queryKey: readonly unknown[]): boolean =>
  (queryKey[1] as { unread?: boolean } | undefined)?.unread === true;

/**
 * Tira das listas "Não lidas" as conversas já lidas, menos a que está aberta
 * (`keepChatId`). Quem lê não espera o socket: a leitura zera o contador no
 * cache (`updateUnreadCountInCache`) e esta poda decide quando a linha sai.
 * O total da página 1 (o número da aba) desce junto.
 */
export function pruneReadFromUnreadLists(
  queryClient: QueryClient,
  keepChatId: DocID | null
): void {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: ["chats"],
    predicate: (q) => isUnreadListKey(q.queryKey),
  });

  for (const query of queries) {
    queryClient.setQueryData<ChatsInfinite>(query.queryKey, (old) => {
      if (!old) return old;
      const isRead = (chat: { documentId?: string; unread_count?: number }) =>
        (chat.unread_count ?? 0) <= 0 && chat.documentId !== keepChatId;
      const removed = old.pages.reduce(
        (count, page) => count + page.data.filter(isRead).length,
        0
      );
      if (removed === 0) return old;
      return {
        ...old,
        pages: old.pages.map((page, index) => {
          const data = page.data.filter((chat) => !isRead(chat));
          const total = page.meta?.pagination?.total;
          if (index !== 0 || total === undefined) return { ...page, data };
          return {
            ...page,
            data,
            meta: {
              ...page.meta,
              pagination: {
                ...page.meta.pagination,
                total: Math.max(0, total - removed),
              },
            },
          };
        }),
      };
    });
  }
}

type ChatsPage = ChatsInfinite["pages"][number];

/**
 * O servidor não devolve a conversa aberta que já foi lida: ela só fica na
 * lista "Não lidas" pela regra desta tela (`keptReadChatId`). Num refetch
 * (invalidação, troca de key), a linha volta para o lugar que ocupava na
 * mesma página, e o total conta com ela. Se o socket já a tirou do cache
 * (encerrada, transferida), não volta.
 */
export function keepReadRowOnRefetch(
  page: ChatsPage,
  previous: ChatsInfinite | undefined,
  pageParam: unknown,
  keepChatId: DocID | null
): ChatsPage {
  if (!keepChatId || !previous) return page;
  if (page.data.some((chat) => chat.documentId === keepChatId)) return page;
  const oldRows = previous.pages[previous.pageParams.indexOf(pageParam)]?.data;
  const at = oldRows?.findIndex((chat) => chat.documentId === keepChatId) ?? -1;
  if (!oldRows || at < 0) return page;

  const data = [...page.data];
  data.splice(Math.min(at, data.length), 0, oldRows[at]);
  const total = page.meta?.pagination?.total;
  if (total === undefined) return { ...page, data };
  return {
    ...page,
    data,
    meta: {
      ...page.meta,
      pagination: { ...page.meta.pagination, total: total + 1 },
    },
  };
}
